import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import type { RuleSource, Severity } from "@backstory/protocol";
import { BusyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, Switch, useToast } from "../../components/ui";
import { formatRelative } from "../../lib/format";
import { SEVERITIES, SIGNAL_KINDS, SOURCES, channelTypeLabel, notificationsApi, notifyKeys, type ChannelView, type Rule, type RuleInput } from "./api";
import { NotificationsTabs } from "./components";
import { settingsEqual, UnsavedGuard } from "../../components/UnsavedChanges";
import { kindLabel } from "../insights/components";
import { formatOffset, timezoneGroups } from "../../lib/timezones";
import { useCan } from "../../auth/roles";
import { EmptyBellMark, EmptyPlugMark, EmptyState, ErrorState } from "../../components/States";

type Draft = {
  name: string;
  source: RuleSource;
  enabled: boolean;
  channelIds: string[];
  cooldownMinutes: number;
  minSeverity: Severity | "";
  kinds: string[];
  routes: string;
  releases: string;
  countInWindow: { count: string; windowMinutes: string };
  affectedUsersAtLeast: string;
  regressionFactor: string;
  customName: string;
  customProps: string; // key=value per line
  usageMeter: string;
  usagePct: string;
  digest: "" | "15m" | "1h" | "1d";
  quietEnabled: boolean;
  quietStart: string;
  quietEnd: string;
  quietTz: string;
  quietSeverityBelow: Severity;
  escalationEnabled: boolean;
  escalationAfter: string;
  escalationChannelIds: string[];
};

const emptyDraft = (): Draft => ({
  name: "",
  source: "cluster.created",
  enabled: true,
  channelIds: [],
  cooldownMinutes: 60,
  minSeverity: "",
  kinds: [],
  routes: "",
  releases: "",
  countInWindow: { count: "", windowMinutes: "" },
  affectedUsersAtLeast: "",
  regressionFactor: "",
  customName: "",
  customProps: "",
  usageMeter: "",
  usagePct: "",
  digest: "",
  quietEnabled: false,
  quietStart: "22:00",
  quietEnd: "07:00",
  quietTz: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  quietSeverityBelow: "critical",
  escalationEnabled: false,
  escalationAfter: "30",
  escalationChannelIds: [],
});

function fromRule(r: Rule): Draft {
  const d = emptyDraft();
  d.name = r.name;
  d.source = r.source;
  d.enabled = r.enabled;
  d.channelIds = r.channelIds;
  d.cooldownMinutes = r.cooldownMinutes;
  d.minSeverity = (r.conditions.minSeverity ?? "") as Draft["minSeverity"];
  d.kinds = r.conditions.kinds ?? [];
  d.routes = (r.conditions.routes ?? []).join("\n");
  d.releases = (r.conditions.releases ?? []).join("\n");
  d.countInWindow = { count: r.conditions.countInWindow ? String(r.conditions.countInWindow.count) : "", windowMinutes: r.conditions.countInWindow ? String(r.conditions.countInWindow.windowMinutes) : "" };
  d.affectedUsersAtLeast = r.conditions.affectedUsersAtLeast ? String(r.conditions.affectedUsersAtLeast) : "";
  d.regressionFactor = r.conditions.regressionFactor ? String(r.conditions.regressionFactor) : "";
  d.customName = r.conditions.customEvent?.name ?? "";
  d.customProps = Object.entries(r.conditions.customEvent?.props ?? {}).map(([k, v]) => `${k}=${v}`).join("\n");
  d.usageMeter = r.conditions.usageMeter ?? "";
  d.usagePct = r.conditions.usagePct ? String(r.conditions.usagePct) : "";
  d.digest = (r.digest?.every ?? "") as Draft["digest"];
  if (r.quietHours) {
    d.quietEnabled = true;
    d.quietStart = r.quietHours.start;
    d.quietEnd = r.quietHours.end;
    d.quietTz = r.quietHours.timezone;
    d.quietSeverityBelow = r.quietHours.severityBelow;
  }
  if (r.escalation) {
    d.escalationEnabled = true;
    d.escalationAfter = String(r.escalation.afterMinutes);
    d.escalationChannelIds = r.escalation.channelIds;
  }
  return d;
}

export function validateDraft(d: Draft): Record<string, string> {
  const errs: Record<string, string> = {};
  if (!d.name.trim()) errs.name = "Give the rule a name.";
  if (d.channelIds.length === 0) errs.channelIds = "Pick at least one channel.";
  if (!(d.cooldownMinutes >= 0 && d.cooldownMinutes <= 10080)) errs.cooldownMinutes = "Cooldown must be 0–10080 minutes.";
  if (d.source === "custom" && !d.customName.trim()) errs.customName = "Custom rules need an event name.";
  if (d.countInWindow.count && !(Number(d.countInWindow.count) > 0 && Number(d.countInWindow.windowMinutes) > 0)) errs.countInWindow = "Count and window must both be positive.";
  if (d.quietEnabled && !/^\d{2}:\d{2}$/.test(d.quietStart)) errs.quietStart = "Use HH:MM.";
  if (d.quietEnabled && !/^\d{2}:\d{2}$/.test(d.quietEnd)) errs.quietEnd = "Use HH:MM.";
  if (d.escalationEnabled && !(Number(d.escalationAfter) > 0)) errs.escalationAfter = "Escalate after a positive number of minutes.";
  if (d.escalationEnabled && d.escalationChannelIds.length === 0) errs.escalationChannelIds = "Pick an escalation channel.";
  return errs;
}

export function toInput(d: Draft): RuleInput {
  const lines = (s: string) => s.split("\n").map((x) => x.trim()).filter(Boolean);
  const props: Record<string, string> = {};
  for (const line of lines(d.customProps)) {
    const [k, ...rest] = line.split("=");
    if (k) props[k.trim()] = rest.join("=").trim();
  }
  const conditions: Rule["conditions"] = {};
  if (d.minSeverity) conditions.minSeverity = d.minSeverity;
  if (d.kinds.length) conditions.kinds = d.kinds as Rule["conditions"]["kinds"];
  if (lines(d.routes).length) conditions.routes = lines(d.routes);
  if (lines(d.releases).length) conditions.releases = lines(d.releases);
  if (d.countInWindow.count) conditions.countInWindow = { count: Number(d.countInWindow.count), windowMinutes: Number(d.countInWindow.windowMinutes) };
  if (d.affectedUsersAtLeast) conditions.affectedUsersAtLeast = Number(d.affectedUsersAtLeast);
  if (d.regressionFactor) conditions.regressionFactor = Number(d.regressionFactor);
  if (d.source === "custom" || d.customName) conditions.customEvent = { name: d.customName.trim(), props: Object.keys(props).length ? props : undefined };
  if (d.usageMeter) conditions.usageMeter = d.usageMeter as Rule["conditions"]["usageMeter"];
  if (d.usagePct) conditions.usagePct = Number(d.usagePct);
  const input: RuleInput = {
    name: d.name.trim(),
    source: d.source,
    enabled: d.enabled,
    channelIds: d.channelIds,
    cooldownMinutes: d.cooldownMinutes,
    conditions,
  };
  if (d.digest) input.digest = { every: d.digest };
  else input.clearDigest = true;
  if (d.quietEnabled) input.quietHours = { start: d.quietStart, end: d.quietEnd, timezone: d.quietTz, severityBelow: d.quietSeverityBelow };
  else input.clearQuietHours = true;
  if (d.escalationEnabled) input.escalation = { afterMinutes: Number(d.escalationAfter), channelIds: d.escalationChannelIds };
  else input.clearEscalation = true;
  return input;
}

export function RulesPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const rules = useQuery({ queryKey: notifyKeys.rules(orgId), queryFn: () => notificationsApi.rules(orgId) });
  const channels = useQuery({ queryKey: notifyKeys.channels(orgId), queryFn: () => notificationsApi.channels(orgId) });
  const can = useCan();
  const [editing, setEditing] = useState<{ id?: string; draft: Draft; initial: Draft } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleting, setDeleting] = useState<Rule | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const invalidate = () => qc.invalidateQueries({ queryKey: notifyKeys.rules(orgId) });

  const save = useMutation({
    mutationFn: async ({ id, draft }: { id?: string; draft: Draft }) => (id ? notificationsApi.patchRule(orgId, id, toInput(draft)) : notificationsApi.createRule(orgId, toInput(draft))),
    onSuccess: () => {
      setEditing(null);
      void invalidate();
      toast.push("info", "Rule saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => notificationsApi.patchRule(orgId, id, { enabled }),
    onSuccess: () => void invalidate(),
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const del = useMutation({
    mutationFn: (id: string) => notificationsApi.deleteRule(orgId, id),
    onSuccess: () => {
      setDeleting(null);
      void invalidate();
      toast.push("info", "Rule deleted.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  const channelName = (id: string) => channels.data?.find((c) => c.id === id)?.name ?? id;
  // Channel ids on a rule that still resolve to a channel. A removed channel
  // leaves nothing behind in the UI.
  const liveChannelIds = (ids: string[]) => (channels.isSuccess ? ids.filter((id) => channels.data.some((c) => c.id === id)) : ids);
  const sourceLabel = (s: RuleSource) => SOURCES.find((x) => x.id === s)?.label ?? s;
  const noChannels = channels.isSuccess && channels.data.length === 0;
  const channelsLink = (
    <Link className="btn primary" to={"/orgs/$org/notifications/channels" as never} params={{ org: orgId } as never}>
      Add a channel
    </Link>
  );
  const dirty = !!editing && !settingsEqual(editing.draft, editing.initial);
  const closeEditor = () => {
    if (confirmDiscard) return;
    if (dirty) {
      setConfirmDiscard(true);
      return;
    }
    setEditing(null);
  };

  const openNew = () => { const draft = emptyDraft(); setErrors({}); setEditing({ draft, initial: draft }); };
  const saveRule = () => {
    if (!editing) return;
    const errs = validateDraft(editing.draft);
    setErrors(errs);
    if (Object.keys(errs).length === 0) save.mutate(editing);
  };

  return (
    <>
      <PageHeader
        title="Notifications"
        actions={
          can.develop && (
            <button
              className="btn primary"
              disabled={noChannels || channels.isPending}
              title={noChannels ? "Add a channel before creating a rule" : undefined}
              onClick={openNew}
            >
              New rule
            </button>
          )
        }
      >
        <NotificationsTabs orgId={orgId} />
      </PageHeader>
      {editing && (
        <RuleEditor
          title={editing.id ? "Edit rule" : "New rule"}
          draft={editing.draft}
          errors={errors}
          channels={channels.data ?? []}
          channelsLink={
            <Link className="btn ghost sm" to={"/orgs/$org/notifications/channels" as never} params={{ org: orgId } as never}>
              Add a channel
            </Link>
          }
          onChange={(draft) => setEditing({ ...editing, draft })}
          onClose={closeEditor}
          onSave={saveRule}
          saving={save.isPending}
          dirty={dirty}
        />
      )}
      {noChannels && rules.isSuccess && rules.data.length > 0 && (
        <div className="card" style={{ marginBottom: 12 }}>
          <EmptyState mark={<EmptyPlugMark />} title="No channels connected" hint="Rules stay paused until there is somewhere to send them." action={can.develop && channelsLink} />
        </div>
      )}
      <div className="card">
        {rules.isError ? (
          <ErrorState error={rules.error} retry={() => void rules.refetch()} />
        ) : rules.isSuccess && rules.data.length === 0 ? (
          noChannels ? (
            <EmptyState mark={<EmptyPlugMark />} title="No channels connected" hint="Add a channel, then create a rule." action={can.develop && channelsLink} />
          ) : (
            <EmptyState
              mark={<EmptyBellMark />}
              title="No rules yet"
              action={can.develop && <button className="btn primary" onClick={openNew}>New rule</button>}
            />
          )
        ) : (
        <div className="rules-table">
        <DataTable
          ariaLabel="Notification rules"
          rows={rules.data ?? []}
          loading={rules.isPending}
          rowKey={(r) => r.id}
          columns={[
            {
              key: "on",
              header: "On",
              render: (r) => {
                const unrouted = liveChannelIds(r.channelIds).length === 0;
                return (
                  <Switch
                    checked={r.enabled && !unrouted}
                    disabled={unrouted}
                    onChange={(v) => toggle.mutate({ id: r.id, enabled: v })}
                    label={<span className="sr-only">Enable {r.name}</span>}
                    title={unrouted ? "Pick a channel first" : undefined}
                  />
                );
              },
            },
            { key: "name", header: "Rule", render: (r) => (<><strong>{r.name}</strong><div className="muted small">{sourceLabel(r.source)}{r.projectId ? ` · project ${r.projectId}` : " · all projects"}</div></>) },
            { key: "cond", header: "Conditions", label: "Conditions", render: (r) => <ConditionSummary rule={r} /> },
            { key: "ch", header: "Channels", label: "Channels", render: (r) => { const ids = liveChannelIds(r.channelIds); return <span className="pill-row">{ids.length ? ids.map((id) => <span key={id} className="badge neutral">{channelName(id)}</span>) : <span className="muted">none</span>}</span>; } },
            { key: "opts", header: "Options", label: "Options", render: (r) => (<span className="muted small">cooldown {r.cooldownMinutes}m{r.digest ? ` · digest ${r.digest.every}` : ""}{r.quietHours ? ` · quiet ${r.quietHours.start}–${r.quietHours.end}` : ""}{r.escalation ? ` · escalate ${r.escalation.afterMinutes}m` : ""}</span>) },
            { key: "fired", header: "Last fired", label: "Last fired", render: (r) => (r.lastFiredAt ? formatRelative(r.lastFiredAt) : <span className="muted">never</span>) },
            {
              key: "act",
              header: "",
              align: "right",
              render: (r) => (
                <RowMenu label={`Actions for ${r.name}`} testId={`rule-menu-${r.id}`}>
                  <button
                    role="menuitem"
                    onClick={() => {
                      const draft = fromRule(r);
                      draft.channelIds = liveChannelIds(draft.channelIds);
                      draft.escalationChannelIds = liveChannelIds(draft.escalationChannelIds);
                      setErrors({});
                      setEditing({ id: r.id, draft, initial: draft });
                    }}
                  >
                    Edit
                  </button>
                  <button role="menuitem" className="danger" onClick={() => setDeleting(r)}>Delete</button>
                </RowMenu>
              ),
            },
          ]}
        />
        </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleting}
        danger
        title="Delete rule"
        confirmLabel="Delete rule"
        busy={del.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && del.mutate(deleting.id)}
      >
        <p style={{ margin: 0 }}>
          <strong>{deleting?.name}</strong> stops sending. Notifications it already created stay in the inbox and delivery log.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmDiscard}
        danger
        typeToConfirm={false}
        title="Discard changes"
        confirmLabel="Discard"
        onClose={() => setConfirmDiscard(false)}
        onConfirm={() => {
          setConfirmDiscard(false);
          setEditing(null);
        }}
      >
        <p style={{ margin: 0 }}>This rule has changes you have not saved.</p>
      </ConfirmDialog>
    </>
  );
}

function ConditionSummary({ rule }: { rule: Rule }) {
  const c = rule.conditions;
  const parts: string[] = [];
  if (c.minSeverity) parts.push(`severity ≥ ${c.minSeverity}`);
  if (c.kinds?.length) parts.push(`kinds: ${c.kinds.join(", ")}`);
  if (c.routes?.length) parts.push(`routes: ${c.routes.join(", ")}`);
  if (c.releases?.length) parts.push(`releases: ${c.releases.join(", ")}`);
  if (c.countInWindow) parts.push(`≥ ${c.countInWindow.count} in ${c.countInWindow.windowMinutes}m`);
  if (c.affectedUsersAtLeast) parts.push(`≥ ${c.affectedUsersAtLeast} users`);
  if (c.regressionFactor) parts.push(`${c.regressionFactor}× baseline`);
  if (c.customEvent?.name) parts.push(`event ${c.customEvent.name}`);
  if (c.usageMeter) parts.push(`${c.usageMeter} ≥ ${c.usagePct ?? 80}%`);
  return parts.length ? <span className="small">{parts.join(" · ")}</span> : <span className="muted small">any</span>;
}

const KIND_GROUPS: Array<{ label: string; kinds: Array<(typeof SIGNAL_KINDS)[number]> }> = [
  { label: "Clicks", kinds: ["rage_click", "dead_click", "error_click", "thrashed_cursor"] },
  { label: "Forms", kinds: ["form_abandonment", "field_struggle"] },
  { label: "Requests", kinds: ["failed_request", "slow_request", "broken_resource", "offline_period"] },
  { label: "Page", kinds: ["js_error", "frozen_ui", "layout_instability", "slow_page", "blank_page", "console_error_burst", "crash"] },
  { label: "Navigation", kinds: ["navigation_loop", "back_button_bounce"] },
  { label: "Backend", kinds: ["backend_exception", "backend_error_log", "metric_threshold"] },
  { label: "Other", kinds: ["accessibility_trap", "consent_friction", "monitor_trigger", "custom"] },
];

function KindPicker({ kinds, onChange }: { kinds: string[]; onChange: (next: string[]) => void }) {
  const [open, setOpen] = useState(kinds.length > 0);
  const toggle = (k: string) => onChange(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k]);
  return (
    <div className="kind-picker">
      <div className="kind-summary">
        <strong>{kinds.length === 0 ? "All kinds" : `${kinds.length} selected`}</strong>
        <button type="button" className="btn ghost sm" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{open ? "Done" : "Choose kinds"}</button>
      </div>
      {kinds.length > 0 && (
        <div className="pill-row">
          {kinds.map((k) => (
            <button key={k} type="button" className="badge accent clickable" onClick={() => toggle(k)}>{kindLabel(k as never)} ×</button>
          ))}
        </div>
      )}
      {open && (
        <div className="kind-groups">
          {KIND_GROUPS.map((g) => (
            <div key={g.label} className="kind-group">
              <h4>{g.label}</h4>
              <div className="kind-group-items">
                {g.kinds.map((k) => (
                  <label key={k}>
                    <input type="checkbox" checked={kinds.includes(k)} onChange={() => toggle(k)} autoComplete="off" />
                    {kindLabel(k as never)}
                  </label>
                ))}
              </div>
            </div>
          ))}
          {kinds.length > 0 && <button type="button" className="btn ghost sm" onClick={() => onChange([])}>Match every kind</button>}
        </div>
      )}
    </div>
  );
}

function RuleEditor({ title, draft, errors, channels, channelsLink, onChange, onClose, onSave, saving, dirty }: {
  title: string;
  draft: Draft;
  errors: Record<string, string>;
  channels: ChannelView[];
  channelsLink: ReactNode;
  onChange: (d: Draft) => void;
  onClose: () => void;
  onSave: () => void;
  saving: boolean;
  dirty: boolean;
}) {
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => onChange({ ...draft, [k]: v });
  const groups = useMemo(() => {
    const m = new Map<string, typeof SOURCES>();
    for (const s of SOURCES) {
      if (!s.legacy || s.id === draft.source) m.set(s.group, [...(m.get(s.group) ?? []), s]);
    }
    return [...m.entries()];
  }, [draft.source]);
  const src = SOURCES.find((s) => s.id === draft.source);
  const isCluster = draft.source.startsWith("cluster.");
  const showRegression = ["cluster.regressed", "error.rate", "release.regression", "vitals.regression"].includes(draft.source);
  const toggleIn = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const channelPicker = (id: string, selected: string[], key: "channelIds" | "escalationChannelIds", label: string) => (
    <div id={id} className="rule-choices" role="group" aria-label={label}>
      {channels.map((c) => (
        <label key={c.id} className="rule-choice">
          <input type="checkbox" checked={selected.includes(c.id)} onChange={() => set(key, toggleIn(selected, c.id))} autoComplete="off" />
          <span className="rule-choice-main">
            <span>{c.name}</span>
            <span className="muted small">{channelTypeLabel(c.type)}{c.enabled ? "" : " · off"}</span>
          </span>
        </label>
      ))}
    </div>
  );

  const errorCount = Object.keys(errors).length;
  const tzGroups = useMemo(() => timezoneGroups(draft.quietTz), [draft.quietTz]);

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      subtitle={src ? `${src.group} · ${src.label}` : undefined}
      width={760}
      className="rule-modal"
      footer={
        <>
          {errorCount > 0 && <span className="rule-footer-note danger small">Fix the highlighted fields to save.</span>}
          {errorCount === 0 && dirty && <span className="rule-footer-note muted small">Unsaved changes</span>}
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <BusyButton className="btn primary" onClick={onSave} busy={saving} busyLabel="Saving…" disabled={!dirty}>Save rule</BusyButton>
        </>
      }
    >
      <UnsavedGuard dirty={dirty} />
      <div className="rule-form">
        <section className="rule-section">
          <div className="rule-section-head">
            <h3>Trigger</h3>
            <p>What this rule watches for and what to call it.</p>
          </div>
          <Field label="Name" error={errors.name}>{(p) => <input {...p} className="input" value={draft.name} onChange={(e) => set("name", e.target.value)} placeholder="High-severity issues to #eng-alerts" autoFocus />}</Field>
          <Field label="When" hint={src?.hint}>
            {(p) => (
              <select {...p} className="input" value={draft.source} onChange={(e) => set("source", e.target.value as RuleSource)}>
                {groups.map(([g, items]) => (
                  <optgroup key={g} label={g}>
                    {items.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </optgroup>
                ))}
              </select>
            )}
          </Field>
        </section>

        <section className="rule-section">
          <div className="rule-section-head">
            <h3>Conditions</h3>
            <p>Every condition left blank matches everything.</p>
          </div>
          <div className="rule-grid">
            <Field label="Minimum severity">
              {(p) => (
                <select {...p} className="input" value={draft.minSeverity} onChange={(e) => set("minSeverity", e.target.value as Draft["minSeverity"])}>
                  <option value="">Any severity</option>
                  {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
            </Field>
            <Field label="Affected users, at least">{(p) => <input {...p} className="input" inputMode="numeric" placeholder="Any number" value={draft.affectedUsersAtLeast} onChange={(e) => set("affectedUsersAtLeast", e.target.value)} />}</Field>
            <Field label="Occurrences, at least" error={errors.countInWindow} hint="Checked against the issue's last-hour count.">{(p) => <input {...p} className="input" inputMode="numeric" placeholder="Any number" value={draft.countInWindow.count} onChange={(e) => set("countInWindow", { ...draft.countInWindow, count: e.target.value })} />}</Field>
            <Field label="Within minutes" hint="Matches inside this window group into one notification.">{(p) => <input {...p} className="input" inputMode="numeric" placeholder="60" value={draft.countInWindow.windowMinutes} onChange={(e) => set("countInWindow", { ...draft.countInWindow, windowMinutes: e.target.value })} />}</Field>
            {showRegression && <Field label="Regression factor" hint="3 means three times the hourly baseline.">{(p) => <input {...p} className="input" inputMode="decimal" placeholder="3" value={draft.regressionFactor} onChange={(e) => set("regressionFactor", e.target.value)} />}</Field>}
          </div>
          {isCluster && (
            <Field label="Signal kinds" hint="Leave this on all kinds to match every one.">
              {() => <KindPicker kinds={draft.kinds} onChange={(kinds) => set("kinds", kinds)} />}
            </Field>
          )}
          <div className="rule-grid">
            <Field label="Routes" hint="One per line. /checkout/* matches everything under /checkout.">{(p) => <textarea {...p} className="input" rows={2} value={draft.routes} onChange={(e) => set("routes", e.target.value)} placeholder="/checkout/*" />}</Field>
            <Field label="Releases" hint="One per line. web@4.* matches every 4.x release.">{(p) => <textarea {...p} className="input" rows={2} value={draft.releases} onChange={(e) => set("releases", e.target.value)} placeholder="web@4.*" />}</Field>
          </div>
          {(draft.source === "custom" || draft.source === "monitor.trigger") && (
            <div className="rule-grid">
              <Field label="Event name" error={errors.customName} hint="The name your app tracks the event under.">{(p) => <input {...p} className="input" value={draft.customName} onChange={(e) => set("customName", e.target.value)} placeholder="patient_alarm" />}</Field>
              <Field label="Required properties" hint="One key=value per line.">{(p) => <textarea {...p} className="input" rows={2} value={draft.customProps} onChange={(e) => set("customProps", e.target.value)} placeholder="ward=icu" />}</Field>
            </div>
          )}
          {draft.source === "usage.threshold" && (
            <div className="rule-grid">
              <Field label="Meter">
                {(p) => (
                  <select {...p} className="input" value={draft.usageMeter} onChange={(e) => set("usageMeter", e.target.value)}>
                    <option value="">Any meter</option>
                    <option value="stored_sessions">Stored sessions</option>
                    <option value="custom_events">Custom events</option>
                    <option value="deep_analyses">Deep analyses</option>
                    <option value="canvas_minutes">Visual recording minutes</option>
                    <option value="live_minutes">Live minutes</option>
                  </select>
                )}
              </Field>
              <Field label="At least % of the plan">{(p) => <input {...p} className="input" inputMode="numeric" value={draft.usagePct} onChange={(e) => set("usagePct", e.target.value)} placeholder="80" />}</Field>
            </div>
          )}
        </section>

        <section className="rule-section">
          <div className="rule-section-head">
            <h3>Send to</h3>
            <p>Every selected channel gets the notification.</p>
          </div>
          <Field label="Channels" error={errors.channelIds}>
            {(p) => channels.length === 0
              ? <span id={p.id} className="muted small rule-empty">No channels yet. {channelsLink}</span>
              : channelPicker(p.id, draft.channelIds, "channelIds", "Channels")}
          </Field>
        </section>

        <section className="rule-section">
          <div className="rule-section-head">
            <h3>Delivery</h3>
            <p>How often it sends and when it stays quiet.</p>
          </div>
          <div className="rule-grid">
            <Field label="Cooldown in minutes" error={errors.cooldownMinutes} hint="Within the cooldown, the same issue updates the existing message.">{(p) => <input {...p} className="input" inputMode="numeric" value={draft.cooldownMinutes} onChange={(e) => set("cooldownMinutes", Number(e.target.value))} />}</Field>
            <Field label="Digest" hint="Batch matches into one message.">
              {(p) => (
                <select {...p} className="input" value={draft.digest} onChange={(e) => set("digest", e.target.value as Draft["digest"])}>
                  <option value="">Send immediately</option>
                  <option value="15m">Every 15 minutes</option>
                  <option value="1h">Hourly</option>
                  <option value="1d">Daily</option>
                </select>
              )}
            </Field>
          </div>

          <div className="rule-option">
            <Switch checked={draft.quietEnabled} onChange={(v) => set("quietEnabled", v)} label="Quiet hours" hint="Hold notifications overnight. Severe ones still go out." />
            {draft.quietEnabled && (
              <div className="rule-grid rule-option-body">
                <Field label="From" error={errors.quietStart}>{(p) => <input {...p} className="input" value={draft.quietStart} onChange={(e) => set("quietStart", e.target.value)} placeholder="22:00" />}</Field>
                <Field label="To" error={errors.quietEnd}>{(p) => <input {...p} className="input" value={draft.quietEnd} onChange={(e) => set("quietEnd", e.target.value)} placeholder="07:00" />}</Field>
                <Field label="Time zone" className="rule-span">
                  {(p) => (
                    <select {...p} className="input" value={draft.quietTz} onChange={(e) => set("quietTz", e.target.value)}>
                      {tzGroups.map((g) => (
                        <optgroup key={g.label} label={g.label}>
                          {g.options.map((o) => <option key={o.id} value={o.id}>{o.label} ({formatOffset(o.offsetMinutes)})</option>)}
                        </optgroup>
                      ))}
                    </select>
                  )}
                </Field>
                <Field label="Still send at severity">
                  {(p) => (
                    <select {...p} className="input" value={draft.quietSeverityBelow} onChange={(e) => set("quietSeverityBelow", e.target.value as Severity)}>
                      {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  )}
                </Field>
              </div>
            )}
          </div>

          <div className="rule-option">
            <Switch checked={draft.escalationEnabled} onChange={(v) => set("escalationEnabled", v)} label="Escalate when nobody acknowledges" hint="Send to more channels if the notification is still open after a while." />
            {draft.escalationEnabled && (
              <div className="rule-option-body">
                <div className="rule-grid">
                  <Field label="Escalate after minutes" error={errors.escalationAfter}>{(p) => <input {...p} className="input" inputMode="numeric" value={draft.escalationAfter} onChange={(e) => set("escalationAfter", e.target.value)} />}</Field>
                </div>
                <Field label="Escalation channels" error={errors.escalationChannelIds}>
                  {(p) => channelPicker(p.id, draft.escalationChannelIds, "escalationChannelIds", "Escalation channels")}
                </Field>
              </div>
            )}
          </div>
        </section>

        <section className="rule-section">
          <Switch
            checked={draft.enabled && draft.channelIds.length > 0}
            disabled={draft.channelIds.length === 0}
            onChange={(v) => set("enabled", v)}
            label="Rule is on"
            hint={draft.channelIds.length === 0 ? "Pick at least one channel to turn this rule on." : "Turn it off to keep the rule without sending anything."}
          />
        </section>
      </div>
    </Dialog>
  );
}
